import { createRef, useEffect, useState } from 'react';
import { WrappedFieldProps } from 'redux-form';
import styled from 'styled-components';

import FormError from 'inquiry/components/FormError';
import PinInput, { PinInputProps } from 'inquiry/components/PinInput';
import { containerStyles } from 'inquiry/mixins/form';
import FormControlProps from 'lib/interfaces/FormControlProps';
import { InputTextBaseStyles } from 'lib/interfaces/ui-components';

const Container = styled('div')`
  ${containerStyles}
`;

interface PinInputFieldProps extends Omit<PinInputProps, 'value' | 'onChange'>, FormControlProps {
  id: string;
  className?: string;
  styles?: InputTextBaseStyles;
}

export default function PinInputField(props: PinInputFieldProps & WrappedFieldProps) {
  const {
    input,
    className,
    disabled,
    meta: { error, submitFailed, submitting },
    autoFocus,
    handleSubmitCode,
    styles,
    ...passthroughProps
  } = props;
  const hasError = !!(error && submitFailed);
  const refHiddenInput = createRef<HTMLInputElement>();

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [cachedError, setCachedError] = useState<string>(undefined);

  useEffect(() => {
    // Clear value when there is a server or submit error.
    // Check to see that input length is 4, so we don't clear when we backspace from full input to partial input
    // (which hits the length validation check thus triggering a change in hasError state)
    if (hasError && input.value?.length === 4) {
      // input onChange will cause redux form to clear the submission error, cache it here until the next input
      setCachedError(error);
      input.onChange('');
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      refHiddenInput.current.focus();
    }
  }, [hasError]);

  const handleChange = (value: string) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setCachedError(undefined);
    input?.onChange(value);
  };

  return (
    <Container className={className}>
      <PinInput
        {...passthroughProps}
        {...input}
        onChange={handleChange}
        autoFocus={autoFocus}
        handleSubmitCode={handleSubmitCode}
        disabled={disabled || submitting}
        refHiddenInput={refHiddenInput}
        hasError={hasError || !!cachedError}
      />
      <FormError
        id={`${passthroughProps.id}--error`}
        hasError={hasError || !!cachedError}
        errorMessage={cachedError || error}
        styles={styles}
      />
    </Container>
  );
}
